'use client';
import { useContext, useEffect, useState } from 'react';
import {
  MdPlaylistAdd,
  MdThumbDown,
  MdThumbDownOffAlt,
  MdThumbUp,
  MdThumbUpOffAlt
} from 'react-icons/md';
import { useQuery, useQueryClient, useMutation } from 'react-query';

import { useTranslations } from 'next-intl';
import NextLink from 'next/link';

import ButtonWithLabel from '../../../../components/ButtonWithLabel';
import CarouselSection from '../../../../components/CarouselSection';
import Footer from '../../../../components/Footer';
import ManageListsItemsModal from '../../../../components/ManageListsItemsModal';
import MovieOrTvRating from '../../../../components/MovieOrTvRating';
import Person from '../../../../components/Person';
import WatchedButton from '../../../../components/WatchedButton';
import WatchProvidersList from '../../../../components/WatchProvidersList';
import Wrapper from '../../../../components/Wrapper';
import AuthContext from '../../../../context/AuthContext';
import addZeroToNumber from '../../../../helpers/addZeroToNumber';
import buildRouteUrl from '../../../../helpers/buildRouteUrl';
import useIntl from '../../../../hooks/useIntl';
import api from '../../../../services/api';
import { Episode, Season } from '../../../../types/types';

interface PropTypes {
  initialData: Episode;
}

async function getEpisode(
  episodeId: string,
  params?: any,
  headers?: any
): Promise<{ data: Episode }> {
  const response = await api.get(`/v1/episodes/${episodeId}`, {
    params,
    headers
  });

  return response.data;
}

function ClientEpisode({ initialData }: PropTypes) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const t = useTranslations();

  const auth = useContext(AuthContext);

  const [isManageListsItemsModalOpen, setIsManageListsItemsModalOpen] =
    useState(false);

  const episodeId = initialData._id;

  useEffect(() => {
    document.title = `${initialData.name} - Trakio`;
  }, []);

  const episodeQuery = useQuery<{ data: Episode }>(
    `episode-${episodeId}`,
    async () => {
      return await getEpisode(
        episodeId,
        {
          region: intl.locale
        },
        {
          'accept-language': intl.full_language
        }
      );
    },
    {
      initialData: { data: initialData }
    }
  );

  const { mutate: rateEpisode } = useMutation(
    async ({ episodeId, rating }: { episodeId: string; rating: number }) => {
      const response = await api.post(`/v1/episodes/${episodeId}/rating`, {
        rating
      });

      return response.data;
    },
    {
      onMutate: ({ episodeId, rating }) => {
        const previousEpisode = queryClient.getQueryData(
          `episode-${episodeId}`
        );

        queryClient.setQueryData(`episode-${episodeId}`, (old: any) => {
          if (old) {
            return {
              data: {
                ...old.data,
                user_related: { ...old.data.user_related, user_rating: rating }
              }
            };
          }
        });

        return () =>
          queryClient.setQueryData(`episode-${episodeId}`, previousEpisode);
      },
      onError: (err, _, rollback: any) => rollback()
    }
  );

  const { mutate: addToUserWatchedList } = useMutation(
    async ({
      tvId,
      seasonNumber,
      episodeNumber
    }: {
      tvId: string;
      episodeId: string;
      seasonNumber: number;
      episodeNumber: number;
    }) => {
      const response = await api.post('/v1/me/mark-episode-as-watched', {
        tv_id: tvId,
        season_number: seasonNumber,
        episode_number: episodeNumber
      });

      return response.data;
    },
    {
      onMutate: () => {
        const previousEpisode = queryClient.getQueryData(
          `episode-${episodeId}`
        );

        queryClient.setQueryData(`episode-${episodeId}`, (old: any) => {
          return {
            data: {
              ...old.data,
              user_related: {
                ...old.data.user_related,
                is_watched: true,
                watch_count: 1
              }
            }
          };
        });

        return () =>
          queryClient.setQueryData(`episode-${episodeId}`, previousEpisode);
      },
      onError: (err, _, rollback: any) => rollback(),
      onSuccess: (_, { tvId, seasonNumber }) => {
        queryClient.invalidateQueries(`episode-${episodeId}`);
        queryClient.invalidateQueries(`tv-${tvId}`);
        queryClient.invalidateQueries(`tv-${tvId}-seasons-${seasonNumber}`);
      }
    }
  );

  const { mutate: removeFromUserWatchedList } = useMutation(
    async ({
      tvId,
      seasonNumber,
      episodeNumber
    }: {
      tvId: string;
      episodeId: string;
      seasonNumber: number;
      episodeNumber: number;
    }) => {
      const response = await api.post('/v1/me/unmark-episode-as-watched', {
        tv_id: tvId,
        season_number: seasonNumber,
        episode_number: episodeNumber
      });

      return response.data;
    },
    {
      onMutate: ({ episodeId }) => {
        const previousEpisode = queryClient.getQueryData(
          `episode-${episodeId}`
        );

        queryClient.setQueryData(`episode-${episodeId}`, (old: any) => {
          if (old) {
            return {
              data: {
                ...old.data,
                user_related: { ...old.data.user_related, is_watched: false }
              }
            };
          }
        });

        return () =>
          queryClient.setQueryData(`episode-${episodeId}`, previousEpisode);
      },

      onError: (err, _, rollback: any) => rollback(),
      onSuccess: (_, { tvId, seasonNumber }) => {
        queryClient.invalidateQueries(`tv-${tvId}`);
        queryClient.invalidateQueries(`tv-${tvId}-seasons-${seasonNumber}`);
      }
    }
  );

  const { mutate: markEpisodeAsRewatched } = useMutation(
    async ({
      tvId,
      episodeNumber,
      seasonNumber
    }: {
      tvId: string;
      episodeNumber: number;
      seasonNumber: number;
    }) => {
      const response = await api.post(`/v1/me/mark-episode-as-watched`, {
        tv_id: tvId,
        season_number: seasonNumber,
        episode_number: episodeNumber,
        is_rewatch: true
      });

      return response.data;
    },
    {
      onMutate: () => {
        const previousEpisode = queryClient.getQueryData(
          `episode-${episodeId}`
        );

        queryClient.setQueryData(`episode-${episodeId}`, (old: any) => {
          if (old) {
            return {
              data: {
                ...old.data,
                user_related: {
                  ...old.data.user_related,
                  watch_count: old.data.user_related.watch_count + 1
                }
              }
            };
          }
        });

        return () =>
          queryClient.setQueryData(`episode-${episodeId}`, previousEpisode);
      }
    }
  );

  const { mutate: resetEpisodeWatches } = useMutation(
    async ({
      tvId,
      episodeNumber,
      seasonNumber
    }: {
      tvId: string;
      episodeId: string;
      episodeNumber: number;
      seasonNumber: number;
    }) => {
      const response = await api.post(`/v1/me/unmark-episode-as-watched`, {
        tv_id: tvId,
        season_number: seasonNumber,
        episode_number: episodeNumber,
        reset_watches: true
      });

      return response.data;
    },
    {
      onMutate: ({ tvId, episodeId, episodeNumber, seasonNumber }) => {
        const previousSeason = queryClient.getQueryData(
          `tv-${tvId}-seasons-${seasonNumber}`
        ) as Season;

        queryClient.setQueryData<{ data: Episode } | undefined>(
          `episode-${episodeId}`,
          old => {
            if (old) {
              return {
                data: {
                  ...old.data,
                  user_related: {
                    ...old.data.user_related,
                    is_watched: false
                  }
                }
              };
            }
          }
        );

        queryClient.setQueryData<{ data: Season } | undefined>(
          `tv-${tvId}-seasons-${seasonNumber}`,
          old => {
            if (old) {
              return {
                data: {
                  ...old.data,
                  episodes: old.data.episodes.map((episode: Episode) => {
                    if (episode.episode_number === episodeNumber) {
                      return {
                        ...episode,
                        user_related: {
                          ...episode.user_related,
                          is_watched: false
                        }
                      };
                    }
                    return episode;
                  })
                }
              };
            }
          }
        );

        return () =>
          queryClient.setQueryData(
            `tv-${tvId}-seasons-${seasonNumber}`,
            previousSeason
          );
      },
      onSuccess: (_, { tvId, seasonNumber }) => {
        queryClient.invalidateQueries(`tv-${tvId}-seasons-${seasonNumber}`);
      },
      onError: (err, _, rollback: any) => rollback()
    }
  );

  const { mutate: resetEpisodeRewatches } = useMutation(
    async ({
      tvId,
      episodeNumber,
      seasonNumber
    }: {
      tvId: string;
      episodeNumber: number;
      seasonNumber: number;
    }) => {
      const response = await api.post(`/v1/me/unmark-episode-as-watched`, {
        tv_id: tvId,
        season_number: seasonNumber,
        episode_number: episodeNumber,
        reset_rewatches: true
      });

      return response.data;
    },
    {
      onMutate: () => {
        queryClient.setQueryData<{ data: Episode } | undefined>(
          `episode-${episodeId}`,
          old => {
            if (old) {
              return {
                data: {
                  ...old.data,
                  user_related: {
                    ...old.data.user_related,
                    watch_count: 1
                  }
                }
              };
            }
          }
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries(`episode-${episodeId}`);
      }
    }
  );

  function likeEpisode({ episodeId }: { episodeId: string }) {
    if (episodeQuery.data?.data.user_related.user_rating === 2) {
      rateEpisode({ episodeId, rating: 0 });
    } else {
      rateEpisode({ episodeId, rating: 2 });
    }
  }

  function dislikeEpisode({ episodeId }: { episodeId: string }) {
    if (episodeQuery.data?.data.user_related.user_rating === 1) {
      rateEpisode({ episodeId, rating: 0 });
    } else {
      rateEpisode({ episodeId, rating: 1 });
    }
  }

  function handleAddOrRemoveWatchedList({
    tvId,
    episodeId,
    seasonNumber,
    episodeNumber,
    isWatched
  }: {
    tvId: string;
    episodeId: string;
    seasonNumber: number;
    episodeNumber: number;
    isWatched: boolean;
  }) {
    if (isWatched) {
      removeFromUserWatchedList({
        tvId,
        episodeId,
        seasonNumber,
        episodeNumber
      });
    } else {
      addToUserWatchedList({ tvId, episodeId, seasonNumber, episodeNumber });
    }
  }

  const cast = episodeQuery.data?.data.credits.filter(
    credit =>
      (credit.type === 'cast' || credit.type === 'guest_star') && credit.avatar
  );

  return (
    <Wrapper>
      {!auth.isLoading && auth.isLoggedIn && episodeQuery.data?.data._id && (
        <ManageListsItemsModal
          isOpen={isManageListsItemsModalOpen}
          onClose={() => setIsManageListsItemsModalOpen(false)}
          data={{
            contentId: episodeQuery.data?.data._id,
            contentType: 'episode'
          }}
        />
      )}

      <div className="m-h-[100vh]">
        {episodeQuery.isSuccess && episodeQuery.data?.data?.tv && (
          <div className="flex flex-col w-full">
            <div
              className={`
                flex
                flex-col
                md:flex-row
                px-4 md:px-10
                py-4 md:py-6
                relative
                min-h-[400px]
              `}
              style={{
                backgroundImage: `linear-gradient(rgba(20, 20, 20, 0.958), rgba(20, 20, 20, 0.958)), url(https://www.themoviedb.org/t/p/w1920_and_h800_multi_faces${episodeQuery.data?.data?.wallpaper?.file_path})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover'
              }}
            >
              <div className="flex flex-col w-full z-[2]">
                <div className="flex flex-row justify-between">
                  <div className="flex flex-col">
                    <span className="text-white font-bold">
                      <NextLink
                        href={buildRouteUrl(
                          `/tv/${episodeQuery.data.data.tv._id}`
                        )}
                      >
                        {episodeQuery.data.data.tv.title}
                      </NextLink>{' '}
                      {t('common.episode_season_tag', {
                        season: addZeroToNumber(
                          episodeQuery.data.data.season_number
                        ),
                        episode: addZeroToNumber(
                          episodeQuery.data.data.episode_number
                        )
                      })}
                    </span>
                    <h1 className="text-white text-2xl font-bold">
                      {episodeQuery.data?.data.name}
                    </h1>
                    <p className="w-full lg:w-[60%] text-white text-md line-clamp-6 mt-1">
                      {episodeQuery.data?.data.overview}
                    </p>
                  </div>
                </div>

                <MovieOrTvRating
                  data={{
                    internal_rating: episodeQuery.data?.data.average_rating || 0
                  }}
                />

                {auth.isLoggedIn && episodeQuery.data?.data.user_related && (
                  <div className="flex flex-row mt-6">
                    <ButtonWithLabel
                      className="mr-3"
                      onClick={() =>
                        likeEpisode({ episodeId: episodeQuery.data?.data._id })
                      }
                      label={t('common.like')}
                      icon={
                        episodeQuery.data?.data.user_related.user_rating ===
                        2 ? (
                          <MdThumbUp size={26} className="text-white" />
                        ) : (
                          <MdThumbUpOffAlt size={26} className="text-white" />
                        )
                      }
                    />

                    <ButtonWithLabel
                      className="mr-3"
                      onClick={() =>
                        dislikeEpisode({
                          episodeId: episodeQuery.data?.data._id
                        })
                      }
                      label={t('common.dislike')}
                      icon={
                        episodeQuery.data?.data.user_related.user_rating ===
                        1 ? (
                          <MdThumbDown size={26} className="text-white" />
                        ) : (
                          <MdThumbDownOffAlt size={26} className="text-white" />
                        )
                      }
                    />

                    <ButtonWithLabel
                      className="mr-3"
                      onClick={() => setIsManageListsItemsModalOpen(true)}
                      label={t('episode.list')}
                      icon={<MdPlaylistAdd size={26} className="text-white" />}
                    />
                  </div>
                )}

                {auth.isLoggedIn &&
                  episodeQuery.data?.data.user_related &&
                  !auth.isLoadingSubscription && (
                    <div className="flex flex-row mt-6">
                      {auth.userSubscription?.status !== 'active' && (
                        <WatchedButton
                          btnType="text"
                          isWatched={
                            episodeQuery.data.data.user_related.is_watched
                          }
                          onClick={() => {
                            handleAddOrRemoveWatchedList({
                              tvId: episodeQuery.data.data.tv?._id as string,
                              episodeId,
                              seasonNumber:
                                episodeQuery.data.data.season_number,
                              episodeNumber:
                                episodeQuery.data.data.episode_number,
                              isWatched:
                                episodeQuery.data.data.user_related.is_watched
                            });
                          }}
                        />
                      )}

                      {auth.userSubscription?.status === 'active' &&
                        !episodeQuery.data.data.user_related.is_watched && (
                          <WatchedButton
                            btnType="text"
                            isWatched={
                              episodeQuery.data.data.user_related.is_watched
                            }
                            onClick={() => {
                              handleAddOrRemoveWatchedList({
                                tvId: episodeQuery.data.data.tv?._id as string,
                                episodeId,
                                seasonNumber:
                                  episodeQuery.data.data.season_number,
                                episodeNumber:
                                  episodeQuery.data.data.episode_number,
                                isWatched:
                                  episodeQuery.data.data.user_related.is_watched
                              });
                            }}
                          />
                        )}

                      {auth.userSubscription?.status === 'active' &&
                        episodeQuery.data.data.user_related.is_watched && (
                          <WatchedButton
                            watchCount={
                              episodeQuery.data.data.user_related.watch_count
                            }
                            btnType="text"
                            withOptions
                            isWatched={
                              episodeQuery.data.data.user_related.is_watched
                            }
                            onRewatch={() =>
                              markEpisodeAsRewatched({
                                tvId: episodeQuery.data.data.tv?._id as string,
                                seasonNumber:
                                  episodeQuery.data.data.season_number,
                                episodeNumber:
                                  episodeQuery.data.data.episode_number
                              })
                            }
                            onResetWatches={() =>
                              resetEpisodeWatches({
                                tvId: episodeQuery.data.data.tv?._id as string,
                                episodeId,
                                seasonNumber:
                                  episodeQuery.data.data.season_number,
                                episodeNumber:
                                  episodeQuery.data.data.episode_number
                              })
                            }
                            onResetRewatches={() =>
                              resetEpisodeRewatches({
                                tvId: episodeQuery.data.data.tv?._id as string,
                                seasonNumber:
                                  episodeQuery.data.data.season_number,
                                episodeNumber:
                                  episodeQuery.data.data.episode_number
                              })
                            }
                          />
                        )}
                    </div>
                  )}
              </div>
            </div>

            <div
              className="flex flex-row justify-between items-start flex-wrap px-4 md:px-10
                py-4 md:py-6"
            >
              <div className="w-full lg:w-[75%]">
                <WatchProvidersList
                  id={`episodes-${episodeId}-watch-providers`}
                  data={episodeQuery.data?.data.watch_providers || []}
                  className="mb-6"
                />

                {(cast?.length || 0) > 0 && (
                  <CarouselSection
                    id={`episodes-${episodeId}-cast`}
                    title={t('episode.sections.cast')}
                    type="person"
                    totalItems={cast?.length || 0}
                    className="mb-6"
                  >
                    {cast?.map(item => (
                      <div key={item._id} className="embla__slide">
                        <Person key={item._id} data={item} />
                      </div>
                    ))}
                  </CarouselSection>
                )}
              </div>
              <div className="hidden md:flex flex-col flex-1 px-4 ml-8">
                {episodeQuery.data.data.next_episode && (
                  <div className="flex flex-col mb-6">
                    <span className="text-white">
                      {t('episode.sections.next_episode')}
                    </span>
                    <div className="flex flex-row justify-between items-center rounded-md bg-black hover:bg-gray-200 p-4">
                      <NextLink
                        className="flex flex-col w-full"
                        href={buildRouteUrl(
                          `/episode/${episodeQuery.data.data.next_episode?._id}`
                        )}
                      >
                        <span className="font-bold text-white line-clamp-1">
                          {t('common.episode_season_tag', {
                            season: addZeroToNumber(
                              episodeQuery.data.data.next_episode.season_number
                            ),
                            episode: addZeroToNumber(
                              episodeQuery.data.data.next_episode.episode_number
                            )
                          })}{' '}
                        </span>
                        <span className="text-white line-clamp-1">
                          {episodeQuery.data.data.next_episode.name}
                        </span>
                      </NextLink>
                    </div>
                  </div>
                )}
                {episodeQuery.data.data.prev_episode && (
                  <div className="flex flex-col mb-6">
                    <span className="text-white">
                      {t('episode.sections.prev_episode')}
                    </span>
                    <div className="flex flex-row justify-between items-center rounded-md bg-black hover:bg-gray-200 p-4">
                      <NextLink
                        className="flex flex-col w-full"
                        href={buildRouteUrl(
                          `/episode/${episodeQuery.data.data.prev_episode?._id}`
                        )}
                      >
                        <span className="font-bold text-white line-clamp-1">
                          {t('common.episode_season_tag', {
                            season: addZeroToNumber(
                              episodeQuery.data.data.prev_episode.season_number
                            ),
                            episode: addZeroToNumber(
                              episodeQuery.data.data.prev_episode.episode_number
                            )
                          })}
                        </span>
                        <span className="text-white line-clamp-1">
                          {episodeQuery.data.data.prev_episode.name}
                        </span>
                      </NextLink>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </Wrapper>
  );
}

export default ClientEpisode;
